import { createContext, useContext, useEffect, useState } from "react";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
  CartSummary,
  CartSummaryPrice,
} from "../presentation/screens/order-create/models/CartItem";
import { NotImplemented } from "../presentation/shared/functions/not-implemented.function";
import { useRepositoriesContext } from "./RepositoriesContext";
import { useProfileContext } from "./ProfileContext";
import { parseJSONWithFallback } from "../utils/functions/JsonParse";
import { Order } from "../core/models/entities/Order";
import { LogoType } from "../core/models/entities/BaseLogo";
import {
  getProductPrice,
  getDesignProductPrice,
} from "../core/models/entities/BaseLogo";
import { Price } from "../core/models/entities/Price";
import { useCartSync } from "../hooks/useCartSync";
import { useErrorToast } from "../presentation/components/Toast/Toast";
import { Role } from "../core/models/entities/Role";

// Helper function to calculate startup costs with staircase pricing
function getStartupCostPrice(
  feePrices: Price[],
  quantity: number
): { adjustedPrice: number; adjustedCustomerPrice?: number } {
  if (!feePrices || feePrices.length === 0) {
    return { adjustedPrice: 0 };
  }

  // Sort prices by minQuantity in descending order to find the appropriate price tier
  const sortedPrices = [...feePrices].sort(
    (a, b) => b.minQuantity - a.minQuantity
  );

  // Find the first price where quantity >= minQuantity
  const applicablePrice = sortedPrices.find((price) => {
    const isApplicable = quantity >= price.minQuantity;
    return isApplicable;
  });

  // If no applicable price found, use the highest minQuantity price (lowest quantity tier)
  const priceToUse = applicablePrice || sortedPrices[sortedPrices.length - 1];

  return {
    adjustedPrice: priceToUse?.adjustedPrice || 0,
    adjustedCustomerPrice: priceToUse?.adjustedCustomerPrice,
  };
}

import {
  HeatingOption,
  ExtraLogo,
} from "../presentation/screens/order-confirmation/dialogs/add-heating-fee-order-dialog";

// Frontend cart summary calculation functions
const calculateCartSummary = (
  items: CartItem[],
  role: Role | undefined,
  heatingOption: HeatingOption = HeatingOption.none,
  extraLogos: ExtraLogo[] = []
): CartSummary => {
  const prices: CartSummaryPrice[] = [];
  let totalQuantity = 0;
  let totalPrice = 0;
  let totalUnitPrice = 0;

  const isCustomer = role !== Role.Vendor;

  const getEffectivePrice = (
    adjustedPrice: number,
    adjustedCustomerPrice?: number | null
  ) => {
    if (
      isCustomer &&
      adjustedCustomerPrice !== undefined &&
      adjustedCustomerPrice !== null &&
      adjustedCustomerPrice > 0
    ) {
      return adjustedCustomerPrice;
    }
    return adjustedPrice;
  };

  // Track storage usage across items to prevent double counting
  const logoStorageUsage = new Map<number, number>();

  items.forEach((item) => {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;

      // Calculate quantity based on logo type
      let deliveryQty = 0;
      let storageQty = 0;

      switch (logoItem.baseLogo.logoType) {
        case LogoType.ImageLogo:
        case LogoType.TextLogo:
        case LogoType.SymbolLogo:
          // Static logos: simple quantity calculation
          deliveryQty = logoItem.toDelivery;
          storageQty = logoItem.toStorage;
          break;

        case LogoType.NumberLogo:
          // Number logos: calculate total quantity from delivery and storage arrays
          // For each number entry, multiply quantity by number of digits in the label
          deliveryQty =
            logoItem.deliveryQuantities?.reduce((sum, q) => {
              return sum + q.quantity * (q.label?.length || 1);
            }, 0) || 0;

          storageQty =
            logoItem.storageQuantities?.reduce((sum, q) => {
              return sum + q.quantity * (q.label?.length || 1);
            }, 0) || 0;
          break;

        case LogoType.NameLogo:
          // Name logos: count as 1 item, quantity comes from quantity selectors
          deliveryQty =
            logoItem.deliveryQuantities?.reduce(
              (sum, q) => sum + q.quantity,
              0
            ) || 0;

          storageQty =
            logoItem.storageQuantities?.reduce(
              (sum, q) => sum + q.quantity,
              0
            ) || 0;
          break;
      }

      const itemQuantity = deliveryQty + storageQty;

      // Storage Check Logic
      const logoId = logoItem.baseLogo.id;
      const currentUsage = logoStorageUsage.get(logoId) ?? 0;
      const inStorage = logoItem.baseLogo.inStorage ?? 0;
      const remainingStorage = inStorage - currentUsage;

      // If remaining storage covers the delivery request, it's considered "free" (0 cost)
      // We only check against deliveryQty because storageQty implies new production
      const isCoveredByStorage = remainingStorage >= deliveryQty;

      const chargeableDeliveryQty = isCoveredByStorage ? 0 : deliveryQty;
      const chargeableQuantity = chargeableDeliveryQty + storageQty;

      if (isCoveredByStorage) {
        logoStorageUsage.set(logoId, currentUsage + deliveryQty);
      }

      let itemPrice = 0;

      // Calculate price using price staircase
      if (logoItem.baseLogo.product?.prices && chargeableQuantity > 0) {
        const productPrice = getProductPrice(
          logoItem.baseLogo.product.prices,
          chargeableQuantity
        );
        if (productPrice) {
          const effectiveUnitPrice = getEffectivePrice(
            productPrice.adjustedPrice,
            productPrice.adjustedCustomerPrice
          );
          itemPrice = effectiveUnitPrice * chargeableQuantity;
          totalUnitPrice += effectiveUnitPrice;

          prices.push({
            productNo: logoItem.baseLogo.product?.sku || "",
            productName: logoItem.title,
            productType: "Logo",
            relatedTo: "",
            toStorage: logoItem.toStorage,
            toDelivery: logoItem.toDelivery,
            price: itemPrice,
            unitPrice: productPrice.adjustedPrice,
            customerPrice: productPrice.adjustedCustomerPrice,
          });
        }
      }

      totalQuantity += itemQuantity;
      totalPrice += itemPrice;

      // Add additional fees
      if (logoItem.baseLogo.product?.additionalFees && chargeableQuantity > 0) {
        logoItem.baseLogo.product.additionalFees.forEach((fee) => {
          let feeTotal = 0;
          let unitPriceForSummary = fee.unitPrice; // Default to original unit price
          let customerPriceForSummary: number | undefined = undefined;
          let effectiveUnitPrice = fee.unitPrice;

          // Special handling for startup costs with quantity-dependent pricing
          if (
            fee.name.toLowerCase().includes("startup") ||
            fee.name.toLowerCase().includes("start-up") ||
            fee.name.toLowerCase().includes("opstart")
          ) {
            // For startup costs, use staircase pricing based on the total item quantity
            const startupPrice = getStartupCostPrice(
              fee.prices,
              chargeableQuantity
            );
            // Multiply by the actual number of colors in the logo
            const numberOfColors = logoItem.baseLogo.colors?.length || 1;
            effectiveUnitPrice = getEffectivePrice(
              startupPrice.adjustedPrice,
              startupPrice.adjustedCustomerPrice
            );
            feeTotal = effectiveUnitPrice * numberOfColors;
            unitPriceForSummary = startupPrice.adjustedPrice; // Use the calculated price per color as unit price
            customerPriceForSummary = startupPrice.adjustedCustomerPrice;
          } else {
            // For regular fees (like film charges), use fixed pricing
            // Try to find customer price if available in prices array
            if (fee.prices && fee.prices.length > 0) {
              const feePrice = getProductPrice(fee.prices, fee.quantity || 1);
              if (feePrice) {
                customerPriceForSummary = feePrice.adjustedCustomerPrice;
                effectiveUnitPrice = getEffectivePrice(
                  fee.unitPrice,
                  feePrice.adjustedCustomerPrice
                );
              }
            }
            feeTotal = effectiveUnitPrice * fee.quantity;
          }

          totalPrice += feeTotal;

          prices.push({
            productNo: fee.itemNo,
            productName: fee.name,
            productType: "Additional Fee",
            relatedTo: logoItem.baseLogo.product?.sku || "",
            toStorage: 0,
            toDelivery: 0,
            price: feeTotal,
            unitPrice: unitPriceForSummary,
            customerPrice: customerPriceForSummary,
          });
        });
      }
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;

      // Designs: sum quantities from all instances
      const itemQuantity = designItem.instances.reduce((sum, instance) => {
        return sum + instance.toDelivery + instance.toStorage;
      }, 0);
      let itemPrice = 0;

      // Calculate design price
      if (designItem.design.product?.totalPrices && itemQuantity > 0) {
        const designPrice = getDesignProductPrice(
          designItem.design.product.totalPrices,
          itemQuantity
        );
        if (designPrice) {
          const effectiveUnitPrice = getEffectivePrice(
            designPrice.adjustedPrice,
            designPrice.adjustedCustomerPrice
          );
          itemPrice = effectiveUnitPrice * itemQuantity;
          totalUnitPrice += effectiveUnitPrice;

          prices.push({
            productNo: designItem.design.product?.mul || "",
            productName: designItem.title,
            productType: "Design",
            relatedTo: "",
            toStorage: 0,
            toDelivery: 0,
            price: itemPrice,
            unitPrice: designPrice.adjustedPrice,
            customerPrice: designPrice.adjustedCustomerPrice,
          });
        }
      }

      // Calculate heating fees if applicable
      // Only apply if heating option is ALL, or if PARTLY and this design/logo is selected
      // For now, handling 'ALL' case for designs
      if (
        heatingOption === HeatingOption.all &&
        designItem.design.heating?.prices
      ) {
        // Heating is applied per instance-logo
        // So we need to sum up (quantity of each instance * number of logos in that instance)
        // Wait, heating usually refers to the *application* of logos onto the garment.
        // If "heating" means "heat transfer application", it's typically per logo application.
        // Let's check how it's done in useOrderActionHandler.ts -> applyHeatingOption
        // It sums up: instQty * inst.logos.length.

        const heatingQty = designItem.instances.reduce((total, inst) => {
          const instQty = (inst.toDelivery ?? 0) + (inst.toStorage ?? 0);
          const instanceMultiplier = inst.logos.length;
          return total + instQty * instanceMultiplier;
        }, 0);

        if (heatingQty > 0) {
          const heatingPrice = getProductPrice(
            designItem.design.heating.prices,
            heatingQty
          );
          if (heatingPrice) {
            const effectiveUnitPrice = getEffectivePrice(
              heatingPrice.adjustedPrice,
              heatingPrice.adjustedCustomerPrice
            );
            const heatingTotal = effectiveUnitPrice * heatingQty;
            totalPrice += heatingTotal;
            prices.push({
              productNo: designItem.design.heating.sku,
              productName: designItem.design.heating.name,
              productType: "Heating",
              relatedTo: designItem.title,
              toStorage: 0,
              toDelivery: 0,
              price: heatingTotal,
              unitPrice: heatingPrice.adjustedPrice,
              customerPrice: heatingPrice.adjustedCustomerPrice,
            });
          }
        }
      }

      // Calculate fees for logos in design
      if (designItem.design.designLogos) {
        designItem.design.designLogos.forEach((designLogo) => {
          const baseLogo = designLogo.logo;
          if (!baseLogo) return;

          const logoId = baseLogo.id;
          const quantityNeeded = itemQuantity;

          const currentUsage = logoStorageUsage.get(logoId) ?? 0;
          const inStorage = baseLogo.inStorage ?? 0;
          const remainingStorage = inStorage - currentUsage;

          const isCoveredByStorage = remainingStorage >= quantityNeeded;

          if (isCoveredByStorage) {
            logoStorageUsage.set(logoId, currentUsage + quantityNeeded);
            // No fees if covered by storage
            return;
          }

          // Not covered: Add fees
          if (baseLogo.product?.additionalFees) {
            baseLogo.product.additionalFees.forEach((fee) => {
              let feeTotal = 0;
              let unitPriceForSummary = fee.unitPrice;
              let customerPriceForSummary: number | undefined = undefined;
              let effectiveUnitPrice = fee.unitPrice;

              if (
                fee.name.toLowerCase().includes("startup") ||
                fee.name.toLowerCase().includes("start-up") ||
                fee.name.toLowerCase().includes("opstart")
              ) {
                const startupPrice = getStartupCostPrice(
                  fee.prices,
                  quantityNeeded
                );
                const numberOfColors = baseLogo.colors?.length || 1;
                effectiveUnitPrice = getEffectivePrice(
                  startupPrice.adjustedPrice,
                  startupPrice.adjustedCustomerPrice
                );
                feeTotal = effectiveUnitPrice * numberOfColors;
                unitPriceForSummary = startupPrice.adjustedPrice;
                customerPriceForSummary = startupPrice.adjustedCustomerPrice;
              } else {
                // Try to find customer price if available in prices array
                if (fee.prices && fee.prices.length > 0) {
                  const feePrice = getProductPrice(
                    fee.prices,
                    fee.quantity || 1
                  );
                  if (feePrice) {
                    customerPriceForSummary = feePrice.adjustedCustomerPrice;
                    effectiveUnitPrice = getEffectivePrice(
                      fee.unitPrice,
                      feePrice.adjustedCustomerPrice
                    );
                  }
                }
                feeTotal = effectiveUnitPrice * fee.quantity;
              }

              totalPrice += feeTotal;

              prices.push({
                productNo: fee.itemNo,
                productName: fee.name,
                productType: "Additional Fee",
                relatedTo: designItem.title,
                toStorage: 0,
                toDelivery: 0,
                price: feeTotal,
                unitPrice: unitPriceForSummary,
                customerPrice: customerPriceForSummary,
              });
            });
          }
        });
      }

      totalQuantity += itemQuantity;
      totalPrice += itemPrice;
    }
  });

  return {
    prices,
    totalQuantity,
    totalPrice,
    totalUnitPrice,
  };
};

export interface OrderBasket {
  items: CartItem[];
  logos: CartItemLogo[];
  designs: CartItemDesign[];
  cartSummary?: CartSummary;
  isUpdating: boolean;
  addLogoItem: (item: CartItemLogo) => Promise<CartItemLogo>;
  addLogoItems: (items: CartItemLogo[]) => Promise<CartItemLogo[]>;
  addDesignItem: (item: CartItemDesign) => Promise<CartItemDesign>;
  addDesignItems: (items: CartItemDesign[]) => Promise<CartItemDesign[]>;
  addItems: (items: CartItem[]) => Promise<CartItem[]>;
  updateItem: (item: CartItem) => Promise<CartItem>;
  removeItem: (id: string) => void;
  clearItems: () => void;
  activeOrder?: Order;
  setActiveOrder: (order?: Order) => void;
  // Cart sync functionality
  isCartSyncing: boolean;
  hasStaleItems: boolean;
  refreshCart: () => void;
  // Heating
  heatingOption: HeatingOption;
  setHeatingOption: (option: HeatingOption) => void;
  extraLogos: ExtraLogo[];
  setExtraLogos: (logos: ExtraLogo[]) => void;
}

const CartContext = createContext<OrderBasket | undefined>(undefined);

interface CartProviderProps {
  children: React.ReactNode;
}

const CartProvider = ({ children }: CartProviderProps): React.ReactElement => {
  const { b2CId, organizationId, role } = useProfileContext();

  const productsKey = `${b2CId}_products`;
  const activeOrderKey = `${b2CId}_activeOrder`;

  const [items, setItems] = useState<CartItem[]>(
    localStorage.getItem(productsKey)
      ? parseJSONWithFallback(
          localStorage.getItem(productsKey) ?? JSON.stringify([]),
          []
        )
      : []
  );
  const [activeOrder, setActiveOrder] = useState<Order | undefined>(
    localStorage.getItem(activeOrderKey)
      ? parseJSONWithFallback(
          localStorage.getItem(activeOrderKey) ?? JSON.stringify([]),
          undefined
        )
      : undefined
  );

  const [cartSummary, setCartSummary] = useState<CartSummary>();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Heating State
  const [heatingOption, setHeatingOption] = useState<HeatingOption>(
    HeatingOption.none
  );
  const [extraLogos, setExtraLogos] = useState<ExtraLogo[]>([]);

  const { orderRepository } = useRepositoriesContext();

  useEffect(() => {
    // Cleanup old localStorage keys to free up space
    if (b2CId) {
      const keysToRemove: string[] = [];
      const currentProductsKey = `${b2CId}_products`;
      const currentActiveOrderKey = `${b2CId}_activeOrder`;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.endsWith("_products") || key.endsWith("_activeOrder")) &&
          key !== currentProductsKey &&
          key !== currentActiveOrderKey
        ) {
          keysToRemove.push(key);
        }
      }

      if (keysToRemove.length > 0) {
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      }
    }
  }, [b2CId]);

  useEffect(() => {
    if (items.length > 0) {
      setIsUpdating(true);
      const newCartSummary = calculateCartSummary(
        items,
        role,
        heatingOption,
        extraLogos
      );
      setCartSummary(newCartSummary);
      setIsUpdating(false);
    } else {
      setCartSummary({
        prices: [],
        totalQuantity: 0,
        totalPrice: 0,
        totalUnitPrice: 0,
      });
    }
  }, [items, heatingOption, extraLogos, role]);

  // Backend cart summary calculation is no longer used
  // All calculations are now done on the frontend

  async function addLogoToCart(item: CartItemLogo): Promise<CartItemLogo> {
    return new Promise<CartItemLogo>((resolve, reject) => {
      if (!shouldAllowPluralCustomerProductsInCart(item)) {
        reject(
          new Error(
            "You are not allowed to operate products of multiple customers at the same time."
          )
        );
        return;
      }

      setItems((state) => {
        const isItemInCart = state.find(
          (cartItem) => cartItem.id === item.id
        ) as CartItemLogo;
        if (isItemInCart) {
          const newState = state.map((cartItem: CartItem) =>
            cartItem.id === item.id ? { ...item } : cartItem
          );
          return newState;
        } else {
          return [...state, { ...item }];
        }
      });

      resolve(item);
    });
  }

  async function addLogosToCart(
    items: CartItemLogo[]
  ): Promise<CartItemLogo[]> {
    return new Promise<CartItemLogo[]>((resolve, reject) => {
      items.map((item) => {
        if (!shouldAllowPluralCustomerProductsInCart(item)) {
          reject(
            new Error(
              "You are not allowed to operate products of multiple customers at the same time."
            )
          );
          return;
        }
      });

      setItems((state) => {
        const newState = items.reduce((acc, item) => {
          const isItemInCart = acc.find(
            (cartItem) => cartItem.id === item.id
          ) as CartItemLogo;
          if (isItemInCart) {
            return acc.map((cartItem: CartItem) =>
              cartItem.id === item.id ? { ...item } : cartItem
            );
          } else {
            return [...acc, { ...item }];
          }
        }, state);

        return newState;
      });

      resolve(items);
    });
  }

  async function addDesignToCart(
    item: CartItemDesign
  ): Promise<CartItemDesign> {
    return new Promise<CartItemDesign>((resolve, reject) => {
      if (!shouldAllowPluralCustomerProductsInCart(item)) {
        reject(
          new Error(
            "You are not allowed to operate products of multiple customers at the same time."
          )
        );
        return;
      }

      setItems((state) => {
        const isItemInCart = state.find(
          (cartItem) => cartItem.id === item.id
        ) as CartItemDesign;
        if (isItemInCart) {
          const newState = state.map((cartItem: CartItem) =>
            cartItem.id === item.id ? { ...item } : cartItem
          );
          return newState;
        } else {
          return [...state, { ...item }];
        }
      });

      resolve(item);
    });
  }

  async function addDesignsToCart(
    items: CartItemDesign[]
  ): Promise<CartItemDesign[]> {
    return new Promise<CartItemDesign[]>((resolve, reject) => {
      items.map((item) => {
        if (!shouldAllowPluralCustomerProductsInCart(item)) {
          reject(
            new Error(
              "You are not allowed to operate products of multiple customers at the same time."
            )
          );
          return;
        }
      });

      setItems((state) => {
        const newState = items.reduce((acc, item) => {
          const isItemInCart = acc.find(
            (cartItem) => cartItem.id === item.id
          ) as CartItemDesign;
          if (isItemInCart) {
            return acc.map((cartItem: CartItem) =>
              cartItem.id === item.id ? { ...item } : cartItem
            );
          } else {
            return [...acc, { ...item }];
          }
        }, state);

        return newState;
      });

      resolve(items);
    });
  }

  async function addItemsToCart(items: CartItem[]): Promise<CartItem[]> {
    return new Promise<CartItem[]>((resolve, reject) => {
      items.map((item) => {
        if (!shouldAllowPluralCustomerProductsInCart(item)) {
          reject(
            new Error(
              "You are not allowed to operate products of multiple customers at the same time."
            )
          );
          return;
        }
      });

      setItems((state) => {
        const newState = items.reduce((acc, item) => {
          const isItemInCart = acc.find(
            (cartItem) => cartItem.id === item.id
          ) as CartItem;
          if (isItemInCart) {
            return acc.map((cartItem: CartItem) =>
              cartItem.id === item.id ? { ...item } : cartItem
            );
          } else {
            return [...acc, { ...item }];
          }
        }, state);

        return newState;
      });

      resolve(items);
    });
  }

  async function updateInCart(item: CartItem): Promise<CartItem> {
    return new Promise<CartItem>((resolve, reject) => {
      if (!shouldAllowPluralCustomerProductsInCart(item)) {
        reject(
          new Error(
            "You are not allowed to operate products of multiple customers at the same time."
          )
        );
        return;
      }

      const isItemInCart = items.find((cartItem) => cartItem.id === item.id);

      if (isItemInCart) {
        setItems((currentItems) =>
          currentItems.map((cartItem: CartItem) =>
            cartItem.id === item.id ? { ...item } : cartItem
          )
        );
        return item;
      } else {
        switch (item.type) {
          case "Logo": {
            addLogoToCart(item as CartItemLogo)
              .then((x) => resolve(x))
              .catch((x) => reject(x));
            break;
          }
          case "Design": {
            addDesignToCart(item as CartItemDesign)
              .then((x) => resolve(x))
              .catch((x) => reject(x));
            break;
          }
        }
      }

      resolve(item);
    });
  }

  const removeFromCart = (id: string): void => {
    const next = items.filter((cartItem) => cartItem.id !== id);

    if (next.length === 0) localStorage.removeItem(productsKey);

    setItems(next);
  };

  const clearCart = (): void => {
    setItems([]);
  };

  function getCustomerId(items: CartItem[]): number | undefined {
    const firstLogo = items.find((x) => x.type === "Logo") as CartItemLogo;
    const firstDesign = items.find(
      (x) => x.type === "Design"
    ) as CartItemDesign;

    if (firstLogo) {
      return firstLogo.baseLogo.customer.parentId;
    }

    if (firstDesign) {
      return firstDesign.design.customer.parentId;
    }

    return undefined;
  }

  function shouldAllowPluralCustomerProductsInCart(item: CartItem): boolean {
    // Disable plural guard for now, as it doesn't align with business logic.
    return true;

    const customerId = getCustomerId(items);

    if (customerId === undefined) {
      return true;
    }

    switch (item.type) {
      case "Logo":
        return (item as CartItemLogo).baseLogo.customer.parentId === customerId;
      case "Design":
        return (item as CartItemDesign).design.customer.parentId === customerId;
      default:
        throw NotImplemented("OrderLine type not implemented");
    }
  }

  function handleSetActiveOrder(order?: Order) {
    setActiveOrder(order);
  }

  // Initialize cart synchronization to keep logo prices up-to-date
  const {
    isRefreshing: isCartSyncing,
    hasStaleItems,
    refreshCart,
  } = useCartSync({
    items,
    updateItem: updateInCart,
  });

  useEffect(() => {
    try {
      localStorage.setItem(productsKey, JSON.stringify(items));
    } catch (error: any) {
      console.error("Failed to save cart to localStorage", error);
      if (
        error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED"
      ) {
        useErrorToast(
          "Storage quota exceeded. Your cart changes may not be saved."
        );
      }
    }
  }, [items]);

  useEffect(() => {
    try {
      localStorage.setItem(activeOrderKey, JSON.stringify(activeOrder));
    } catch (error: any) {
      console.error("Failed to save active order to localStorage", error);
      if (
        error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED"
      ) {
        useErrorToast(
          "Storage quota exceeded. Your active order may not be saved."
        );
      }
    }
  }, [activeOrder]);

  useEffect(() => {
    const cartItems = localStorage.getItem(productsKey);
    if (cartItems) {
      setItems(parseJSONWithFallback(cartItems, []));
    }
  }, []);

  return (
    <CartContext.Provider
      value={{
        items: items,
        cartSummary,
        isUpdating,
        logos: items.filter((x) => x.type === "Logo") as CartItemLogo[],
        designs: items.filter((x) => x.type === "Design") as CartItemDesign[],
        addLogoItem: addLogoToCart,
        addLogoItems: addLogosToCart,
        addDesignItem: addDesignToCart,
        addDesignItems: addDesignsToCart,
        addItems: addItemsToCart,
        updateItem: updateInCart,
        removeItem: removeFromCart,
        clearItems: clearCart,
        activeOrder: activeOrder,
        setActiveOrder: handleSetActiveOrder,
        // Cart sync functionality
        isCartSyncing,
        hasStaleItems,
        refreshCart,
        // Heating
        heatingOption,
        setHeatingOption,
        extraLogos,
        setExtraLogos,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

function useCartContext(): OrderBasket {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCartContext must be used within a CartProvider");
  }
  return context;
}

export { CartProvider, useCartContext };
